import React, { useState, useMemo } from 'react';
import { FilterButtons, LoadingIndicator, useIsFilterModified } from '@ygtechnologies/bygr-frontend-component-library';
import { searchChangeRequests, getChangeRequestFilterData } from '@/services/change-requests';
import ResidentialUnitGrouping from '../residential-units/ResidentialUnitGrouping/ResidentialUnitGrouping';
import { ChangeRequest, BatchedChanges, ChangeRequestWithChatInfo } from '@/types';
import ResidentialUnitGroupingHeader from '../residential-units/ResidentialUnitGrouping/ResidentialUnitGroupingHeader';
import ChangeRequestFilter, { ChangeRequestFilterValue, getInitialChangeRequestFilterValue } from './ChangeRequestFilter';
import { useAsyncState, useCanScroll, useDefaultScrollToId, useAsyncStateWithIsLoading, useQueryState } from '@/utils/hooks';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import ChangeRequestList from './ChangeRequestList';
import { maxDate } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { ButtonBar, SlideToggle } from '@ygtechnologies/rubus-ui';

export type ChangeRequestFilterableListMode = 'all' | 'unassigned' | 'mine';

export const useMappedChangeRequest = (changeRequests: ChangeRequest[] | null) => {
    const commentThreads = useSelector((state: IState) => state.seller.projectOverview.comments);
    const mappedChangeRequests: ChangeRequestWithChatInfo[] | null = useMemo(() => {
        if (!changeRequests) {
            return null;
        }

        if (!commentThreads) {
            return changeRequests.map((cr) => ({ ...cr, unreadMessages: 0, lastMessageDate: null, isFollowing: false }));
        }

        return changeRequests.map((cr) => {
            const publicThread = commentThreads.find((ct) => cr.publicCommentThreadId === ct.threadId);
            const privateThread = commentThreads.find((ct) => cr.privateCommentThreadId === ct.threadId);

            const unreadMessages = (privateThread?.unreadMessages ?? 0) + (publicThread?.unreadMessages ?? 0);
            const lastMessageDate = maxDate([privateThread?.lastMessageDate, publicThread?.lastMessageDate]);

            if (!publicThread && !privateThread) {
                // User is not following either of em.
                // Or there are no messages yet, which is also fine.
            }
            return { ...cr, unreadMessages, lastMessageDate, isFollowing: !!(publicThread || privateThread) };
        });
    }, [commentThreads, changeRequests]);

    return mappedChangeRequests;
};

interface Props {
    mode?: ChangeRequestFilterableListMode;
}
const ChangeRequestFilterableList: React.FC<Props> = ({ mode = 'all' }) => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const [filter, setFilter] = useQueryState<ChangeRequestFilterValue>(getInitialChangeRequestFilterValue());
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterData] = useAsyncState(() => getChangeRequestFilterData(projectId), [], null);
    const defaultExpandedId = useDefaultScrollToId();
    const canScroll = useCanScroll(defaultExpandedId);
    const filterModified = useIsFilterModified(filter, getInitialChangeRequestFilterValue);

    const [changeRequests, setChangeRequests, reloadChangeRequest, isLoading] = useAsyncStateWithIsLoading(
        () =>
            searchChangeRequests({
                units: filter.units,
                groups: filter.groups,
                statuses: filter.statuses,
                participants: filter.participants,
                searchText: filter.searchText,
                includeArchived: filter.includeArchived ? undefined : false,
                projectId,
                onlyUnassigned: mode === 'unassigned',
                onlyMine: mode === 'mine',
            }),
        [filter, mode],
        null
    );
    // const pagination = usePagination(changeRequests?.length);
    const mappedChangeRequests = useMappedChangeRequest(changeRequests);
    const handleChange = (changes: BatchedChanges<ChangeRequest>) => {
        if (!changeRequests) {
            return;
        }
        setChangeRequests(changes.updateArray(changeRequests));
    };

    const handleForceReload = () => {
        reloadChangeRequest();
    };

    if (!mappedChangeRequests) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <ButtonBar sticky margins="none" stickyClassName="stickyBleed">
                <ButtonBar.Right>
                    <SlideToggle
                        onChange={(e) => setFilter({ ...filter, includeArchived: e.target.checked })}
                        checked={filter.includeArchived}>
                        <Trans>Show archived requests</Trans>
                    </SlideToggle>
                    <ChangeRequestFilter
                        isOpen={isFilterOpen}
                        onClose={() => setIsFilterOpen(false)}
                        filterData={filterData}
                        value={filter}
                        onFilterChange={setFilter}
                    />
                    <FilterButtons
                        searchText={filter.searchText}
                        onSearchTextChange={(v) => setFilter({ ...filter, searchText: v })}
                        isLoading={isLoading}
                        onButtonClick={() => setIsFilterOpen((v) => !v)}
                        isActive={filterModified}
                    />
                </ButtonBar.Right>
            </ButtonBar>

            <ResidentialUnitGrouping
                paginate
                projectId={projectId}
                items={mappedChangeRequests}
                emptyText={<ChangeRequestList onChange={() => null} changeRequests={[]} reloadChangeRequests={() => {}} />}>
                {(items, residentialUnit, residentialUnitGroup) => (
                    <React.Fragment key={residentialUnit.id}>
                        <ResidentialUnitGroupingHeader
                            residentialUnit={residentialUnit}
                            residentialUnitGroup={residentialUnitGroup}
                            detailsLinkTab="changes"
                        />
                        <ChangeRequestList
                            defaultExpandedId={canScroll ? defaultExpandedId : undefined}
                            changeRequests={items}
                            reloadChangeRequests={handleForceReload}
                            onChange={handleChange}
                        />
                    </React.Fragment>
                )}
            </ResidentialUnitGrouping>
            {/* <Pagination value={pagination} /> */}
        </>
    );
};

export default ChangeRequestFilterableList;
